import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { DATE_FORMAT_WEEK_DAY_MID_FNS, PeriodTypeEnum, WeekdayEnum } from '@schooly/api';
import {
  Grid,
  GridBody,
  GridCell,
  GridHead,
  GridRow,
  TWO_TONE_CLASSNAME,
  TwoToneBackground,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC, useCallback, useMemo } from 'react';
import { useFormContext, UseFormRegisterReturn } from 'react-hook-form-lts';
import { useIntl } from 'react-intl';

import {
  TimetableStructureContextProps,
  TimetableStructureForm,
} from '../../../../../context/timetableStructure/WithTimetableStructure';
import { resolveStructureRegisterSessions } from '../../../SchoolTimetables/register-session-display';
import { getPeriodTypePalette, getWeekdayLabel } from '../../utils';

interface TimetableStructureSlotsStepProps {
  timeSlotsValidation: UseFormRegisterReturn<'time_slots'>;
  confirmChange: TimetableStructureContextProps['confirmChange'];
}

export const TimetableStructureSlotsStep: FC<TimetableStructureSlotsStepProps> = ({
  timeSlotsValidation,
  confirmChange,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { watch, setValue } = useFormContext<TimetableStructureForm>();

  const periods = watch('periods');
  const weekdays = watch('weekdays');
  const timeSlots = watch('time_slots');

  const registerSessions = useMemo(() => resolveStructureRegisterSessions(periods), [periods]);

  const hasTimeSlot = useCallback(
    (periodId: string, weekday: WeekdayEnum): boolean => {
      return timeSlots.some((slot) => slot.period_id === periodId && slot.weekday === weekday);
    },
    [timeSlots],
  );

  const toggleTimeSlot = useCallback(
    async (periodId: string, weekday: WeekdayEnum) => {
      const existingIndex = timeSlots.findIndex(
        (slot) => slot.period_id === periodId && slot.weekday === weekday,
      );

      if (existingIndex >= 0) {
        const confirmed = await confirmChange({
          type: 'remove_slot',
          id: timeSlots[existingIndex].id ?? null,
        });
        if (!confirmed) return;
        const updatedSlots = timeSlots.filter((_, index) => index !== existingIndex);
        setValue('time_slots', updatedSlots, { shouldDirty: true, shouldValidate: true });
        return;
      }

      const period = periods.find((p) => p.id === periodId);
      if (!period) return;

      const newSlot = {
        period_id: periodId,
        weekday,
        start_time: period.start_time,
        end_time: period.end_time,
      };
      setValue('time_slots', [...timeSlots, newSlot], { shouldDirty: true, shouldValidate: true });
    },
    [timeSlots, periods, setValue, confirmChange],
  );

  if (!periods.length || !weekdays.length) {
    return null;
  }

  return (
    <>
      <input type="hidden" {...timeSlotsValidation} />
      <Grid>
        <GridHead sx={{ '&.MuiTableHead-root .MuiTableCell-root': { height: 30, p: 0.5 } }}>
          <GridRow>
            <GridCell width={100} />
            {weekdays.map((weekday) => (
              <GridCell key={weekday} sx={{ minWidth: 50 }} center>
                <Typography variant="h3" color="text.primary">
                  {getWeekdayLabel(weekday, DATE_FORMAT_WEEK_DAY_MID_FNS).toUpperCase()}
                </Typography>
              </GridCell>
            ))}
          </GridRow>
        </GridHead>

        <GridBody>
          {periods.map((period) => {
            const palette = getPeriodTypePalette(period.type, theme);
            const session = registerSessions.get(period.id) ?? null;
            const secondaryPalette =
              session &&
              period.type === PeriodTypeEnum.Lesson &&
              getPeriodTypePalette(PeriodTypeEnum.Registration, theme);

            return (
              <GridRow key={period.id} withoutDefaultHover>
                <GridCell width={120} borderBottom={false} py={0.5}>
                  <TypographyWithOverflowHint variant="h3" color="text.primary">
                    {period.start_time} - {period.end_time}
                  </TypographyWithOverflowHint>
                </GridCell>

                {weekdays.map((weekday) => {
                  const isActive = hasTimeSlot(period.id, weekday);

                  return (
                    <GridCell key={weekday} borderBottom={false} p={0.25} height={30}>
                      <Tooltip
                        title={formatMessage(
                          {
                            id: isActive
                              ? 'school-timetableStructure-ClickToRemove'
                              : 'school-timetableStructure-ClickToAdd',
                          },
                          { periodName: period.name },
                        )}
                        componentsProps={{ tooltip: { sx: { minWidth: 200, p: 1.25 } } }}
                      >
                        <Box
                          onClick={() => toggleTimeSlot(period.id, weekday)}
                          sx={(theme) => ({
                            position: 'relative',
                            overflow: 'hidden',
                            width: '100%',
                            height: '100%',
                            backgroundColor: isActive ? palette.secondary : 'transparent',
                            border: theme.mixins.borderValue(),
                            borderColor: isActive ? 'transparent' : palette.secondary,
                            borderRadius: theme.spacing(0.5),
                            cursor: 'pointer',
                            [TWO_TONE_CLASSNAME]: {
                              path: {
                                fill: secondaryPalette ? secondaryPalette.secondary : undefined,
                              },
                            },
                            ':hover': {
                              backgroundColor: isActive ? palette.primary : 'transparent',
                              borderColor: isActive ? 'transparent' : palette.primary,
                              [TWO_TONE_CLASSNAME]: {
                                path: {
                                  fill: secondaryPalette ? secondaryPalette.primary : undefined,
                                },
                              },
                            },
                          })}
                        >
                          {isActive && secondaryPalette && <TwoToneBackground />}
                        </Box>
                      </Tooltip>
                    </GridCell>
                  );
                })}
              </GridRow>
            );
          })}
        </GridBody>
      </Grid>
    </>
  );
};
